// RequestsPage — /requests unified outbound-request triage surface.
//
// One filterable table of EVERY tokenized request WHP sends — rental
// verifications, tenant intake (designation / yard-care / pets), lease signing,
// and condition reports — with a normalized sent → opened → completed status and
// a per-row detail drawer showing the collected data. The one action it owns is
// a unified manual RESEND/NUDGE (WEL-20 Phase 2): re-send any outstanding
// request (one, or all outstanding in a household) with a 12h anti-spam confirm
// that resets the recipient's reminder clock. System-specific actions (verdict)
// still live on each page, via the "Open in …" deep link. The detail drawer's
// signer roster also offers the universal doc-type-scoped "Change email" for
// any not-yet-signed signer (same inline pattern as the thread detail).

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import {
  animalsEmptyLabel,
  activityOf,
  cancelRequests,
  listRequests,
  resendRequests,
  reviveDesignation,
  type ActivityEvent,
  type ManualSendEntry,
  type OutboundRequestRow,
  type RecipientActivity,
  type RequestEventKind,
  type RequestStatus,
  getW9DocumentUrl,
} from '@/lib/requestsClient';
import { ApiError } from '@/lib/api';
import { matchesSearchQuery } from '@/lib/requestsSearch';
import { OPAQUE_ID_RE } from '@/lib/noIds';
import { resendMoveoutFundsIntake } from '@/lib/moveoutFundsClient';
import { cancelDepositAgreement } from '@/lib/depositAgreementClient';
import { cancelOwnerIntake, resendOwnerIntake } from '@/lib/ownerIntakeClient';
import {
  cancelAnimalAddendum,
  resendAnimalAddendumIntake,
} from '@/lib/animalAddendumClient';

const TWELVE_H_MS = 12 * 3_600_000;

/** A pending manual-resend confirmation (single row or a household batch). */
interface ResendTarget {
  ids: string[];
  /** Recipient name (single) or household label (bulk), shown in the confirm. */
  label: string;
  /** Of the targets, those emailed within the last 12h (drives the spam warning). */
  recent: OutboundRequestRow[];
}

/** A pending cancel/withdraw confirmation (single row or a bulk selection) — WEL-46. */
interface CancelTarget {
  ids: string[];
  /** Recipient name (single) or "N selected" (bulk), shown in the confirm. */
  label: string;
  /** Whether any of the targets are signature-backed (lease/addendum/condition report). */
  hasSigning: boolean;
  /** Rows dropped from the batch because they own a dedicated Cancel — see openCancel. */
  skippedDedicated: number;
  /**
   * Whether the modal may offer "Notify recipients". False when any target is a
   * W-9: a W-9 has never had a cancellation notice (it could not be cancelled
   * until WEL-181/182), and none is written for it — so no email goes, ever.
   */
  canNotify: boolean;
}
import { useFocusTrap } from '@/lib/useFocusTrap';
import {
  animalKindLabel,
  animalQuestionKind,
  asksAnimalField,
  isAnimalAgeUnit,
} from '@/lib/animalQuestions';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';
import { AppfolioSyncChip } from '@/components/AppfolioSyncChip';
import { CurrentDocumentDownload } from '@/components/CurrentDocumentDownload';
import { SignerEmailSwap } from '@/components/SignerEmailSwap';
// Aliased: this page has a LOCAL `toast` banner state of its own.
import { toast as appToast } from '@/components/Toast';
import { updateSignerEmail, type CurrentDocumentType } from '@/lib/leaseDraftsClient';
import { initialsOf } from '@/lib/senderInitials';

import {
  CANCEL_RDA_KINDS,
  DOCUMENT_KINDS,
  isAnimalAddendumPaid,
  isOwnerIntake,
  isSigningKind,
  isW9Kind,
  KIND_FILTERS,
  kindFilterFromParam,
  matchesKindFilter,
  offersDedicatedCancel,
  offersGenericCancel,
  type KindFilterValue,
  RESEND_INTAKE_KINDS,
  w9VersionIdFromRowId,
  withdrawsDocuSeal,
} from '@/lib/requestsKinds';

// --- Filter option sets (the kind lists live in lib/requestsKinds.ts) --------

type StatusFilter =
  | 'all'
  | 'needs_attention'
  | 'sent'
  | 'opened'
  | 'completed'
  | 'in_appfolio'
  | 'problem'
  | 'cancelled';

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'sent', label: 'Sent (unopened)' },
  { value: 'opened', label: 'Opened' },
  { value: 'completed', label: 'Completed' },
  // WEL-79: filter to documents that have successfully landed in AppFolio. The
  // occupancy-sync chip's only `success` tone IS the "In AppFolio" state, so it's
  // the robust predicate regardless of label text. Only sync-eligible kinds
  // (lease / addendum / condition report / deposit agreement / payment plan) ever
  // carry appfolioSync, so this naturally excludes RV / intake rows.
  { value: 'in_appfolio', label: 'In AppFolio' },
  { value: 'problem', label: 'Problem' },
  // WEL-105 (Andrea): cancelled agreements/requests stay on this page with a
  // neutral "Cancelled" pill — this option lets staff pull them up deliberately
  // (every other option, and the pill-less statuses, naturally exclude them).
  { value: 'cancelled', label: 'Cancelled' },
];

/** Statuses that count as a "problem" (declined / bounced / no-response). */
const PROBLEM_STATUSES = new Set<RequestStatus>(['declined', 'bounced', 'expired']);

/**
 * The sentence the SERVER wrote, or a plain fallback.
 *
 * ApiError.message is "API error 409" — the status, not the reason. The
 * dedicated cancel/resend routes refuse with real guidance in the body ("That
 * addendum is already fully signed, so it cannot be cancelled or resent. To add
 * another animal, send a new animal addendum."), and showing the operator the
 * status code instead throws away the one thing that tells them what to do —
 * precisely in the moment they have no good option in front of them.
 */
function serverMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  return err instanceof Error ? err.message : fallback;
}

function matchesStatusFilter(row: OutboundRequestRow, filter: StatusFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'needs_attention':
      return row.needsAttention;
    case 'sent':
      return row.status === 'sent';
    case 'opened':
      return row.status === 'opened';
    case 'completed':
      return row.status === 'completed';
    case 'in_appfolio':
      return row.appfolioSync?.tone === 'success';
    case 'problem':
      return PROBLEM_STATUSES.has(row.status);
    case 'cancelled':
      return row.status === 'cancelled';
    default:
      return true;
  }
}

// --- Date helpers -----------------------------------------------------------

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Full date + time for the detail modal + activity history — Miles (WEL-47/50):
 * "should be dates and times not just dates." The compact table keeps `fmtDate`;
 * the drawer (where "everything someone would want to know" lives) and the
 * activity history show the precise instant.
 */
function fmtDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relativeAge(iso: string | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) return `${hours}h ago`;
  const mins = Math.floor(ms / 60_000);
  if (mins >= 1) return `${mins}m ago`;
  return 'just now';
}

/** Best-effort designee name from a designation row's collected data (display only). */
function designeeNameOf(row: OutboundRequestRow): string {
  const d = row.data;
  if (d && typeof d === 'object' && 'designeeName' in d) {
    const name = (d as { designeeName?: unknown }).designeeName;
    if (typeof name === 'string') return name.trim();
  }
  return '';
}

/** Tooltip for a row's Resend button — last contact + manual-resend history. */
function resendHint(r: OutboundRequestRow): string {
  const parts: string[] = [];
  if (r.lastSentAt) parts.push(`Last sent ${relativeAge(r.lastSentAt)}`);
  if (r.manualSends && r.manualSends.length > 0) {
    const last = r.manualSends[r.manualSends.length - 1]!;
    parts.push(
      `${r.manualSends.length} manual resend${r.manualSends.length === 1 ? '' : 's'} (last by ${last.by})`,
    );
  }
  return parts.join(' · ') || 'Resend this request';
}

/**
 * WEL-99: the Type-cell label. Renewals share the 'lease' kind (so the "Lease"
 * filter surfaces both new leases AND renewals), but each row renders its own
 * label — "Renewal" when the server flagged it `isRenewal`, else the row's
 * kindLabel. Matches the command center's "Renewal" thread-type label.
 */
function typeLabelOf(row: OutboundRequestRow): string {
  return row.isRenewal ? 'Renewal' : row.kindLabel;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RequestsPage() {
  const [rows, setRows] = useState<OutboundRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The Type filter lives in the URL (`?kind=`) so a filtered view can be
  // linked. kindFilterFromParam maps a retired value — WEL-172 folded
  // "Animal addendum (new animal)" into "Animal addendum" — onto the option
  // that replaced it, so an old link still opens on the right rows.
  const [searchParams, setSearchParams] = useSearchParams();
  const kindFilter = kindFilterFromParam(searchParams.get('kind'));
  const setKindFilter = useCallback(
    (next: KindFilterValue) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'all') params.delete('kind');
          else params.set('kind', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  // WEL-46: multi-select for bulk Cancel / Resend.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listRequests();
      setRows(res.requests);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (!matchesKindFilter(kindFilter, r.kind)) return false;
      if (!matchesStatusFilter(r, statusFilter)) return false;
      // WEL-64: search matches applicant names (Andrea: "right now we can only
      // filter by recipient") + unit/property, not just recipient/email/property.
      if (!matchesSearchQuery(r, query)) return false;
      return true;
    });
  }, [rows, kindFilter, statusFilter, query]);

  const attentionCount = useMemo(() => rows.filter((r) => r.needsAttention).length, [rows]);
  const openRow = useMemo(() => rows.find((r) => r.id === openId) ?? null, [rows, openId]);

  // WEL-20: "Group by application" — roll the filtered rows up per group + order
  // them so each group's rows are adjacent (groups needing attention first).
  const [groupView, setGroupView] = useState(false);
  const groupRollups = useMemo(() => {
    const FALLBACK_LABEL = 'Untitled application';
    const m = new Map<
      string,
      { total: number; completed: number; attention: number; label: string; unit?: string; applicants?: string }
    >();
    for (const r of filtered) {
      const cur =
        m.get(r.groupId) ??
        { total: 0, completed: 0, attention: 0, label: r.groupLabel ?? FALLBACK_LABEL };
      cur.total += 1;
      if (r.status === 'completed') cur.completed += 1;
      if (r.needsAttention) cur.attention += 1;
      if (cur.label === FALLBACK_LABEL && r.groupLabel) cur.label = r.groupLabel;
      // WEL-64: prefer the explicit unit/applicants so the grouped header can read
      // "Unit – Applicants" (there are often several applications per unit).
      if (!cur.unit && r.unit) cur.unit = r.unit;
      if (!cur.applicants && r.applicants) cur.applicants = r.applicants;
      m.set(r.groupId, cur);
    }
    // WEL-64: compose the grouped header label as `${unit} – ${applicants}`,
    // falling back gracefully when either piece is missing (unit only, applicants
    // only, or the existing groupLabel when we have neither).
    for (const cur of m.values()) {
      const composed =
        cur.unit && cur.applicants
          ? `${cur.unit} – ${cur.applicants}`
          : cur.unit ?? cur.applicants;
      if (composed) cur.label = composed;
    }
    return m;
  }, [filtered]);
  // AppFolio occupancy-doc sync is carried on each lease/addendum row of a group
  // (every signer row repeats it) — collapse to one chip per group.
  const groupSync = useMemo(() => {
    const m = new Map<string, OutboundRequestRow['appfolioSync']>();
    for (const r of filtered) {
      if (r.appfolioSync && !m.has(r.groupId)) m.set(r.groupId, r.appfolioSync);
    }
    return m;
  }, [filtered]);
  const displayRows = useMemo(() => {
    if (!groupView) {
      // WEL-50: default the flat view to "date sent" descending (most recently
      // sent first); rows never sent (no sentAt) sink to the bottom, tie-broken
      // by id so the order is stable.
      return [...filtered].sort((a, b) => {
        const as = a.sentAt ?? '';
        const bs = b.sentAt ?? '';
        if (as !== bs) return as < bs ? 1 : -1;
        return a.id.localeCompare(b.id);
      });
    }
    return [...filtered].sort((a, b) => {
      const ra = groupRollups.get(a.groupId)!;
      const rb = groupRollups.get(b.groupId)!;
      if ((ra.attention > 0) !== (rb.attention > 0)) return ra.attention > 0 ? -1 : 1;
      if (a.groupId !== b.groupId) return a.groupId.localeCompare(b.groupId);
      return a.id.localeCompare(b.id);
    });
  }, [filtered, groupView, groupRollups]);
  // Flat view: row ids that own the once-per-group sync chip (first rendered row
  // of each group that has a sync). Grouped view shows it on the group header.
  const syncChipRowIds = useMemo(() => {
    const ids = new Set<string>();
    const seen = new Set<string>();
    for (const r of displayRows) {
      if (seen.has(r.groupId)) continue;
      seen.add(r.groupId);
      if (groupSync.has(r.groupId)) ids.add(r.id);
    }
    return ids;
  }, [displayRows, groupSync]);
  // WEL-48: the first rendered moveout-funds row of each group owns the
  // once-per-agreement "Resend intake" action (it operates on the whole group,
  // not a single signer — so it must not repeat on every signer row).
  const resendIntakeRowIds = useMemo(() => {
    const ids = new Set<string>();
    const seen = new Set<string>();
    for (const r of displayRows) {
      if (!RESEND_INTAKE_KINDS.has(r.kind)) continue;
      if (seen.has(r.groupId)) continue;
      seen.add(r.groupId);
      ids.add(r.id);
    }
    return ids;
  }, [displayRows]);
  // WEL-157: once-per-group Cancel for a live Move-out funds request (either
  // phase — intake out, or agreement out for signing). Cancelled / completed
  // rows have nothing left to cancel.
  const cancelRdaRowIds = useMemo(() => {
    const ids = new Set<string>();
    const seen = new Set<string>();
    for (const r of displayRows) {
      if (!CANCEL_RDA_KINDS.has(r.kind)) continue;
      if (seen.has(r.groupId)) continue;
      seen.add(r.groupId);
      if (!offersDedicatedCancel(r.kind, r.status)) continue;
      ids.add(r.id);
    }
    return ids;
  }, [displayRows]);

  // --- WEL-20 Phase 2: manual resend / nudge --------------------------------
  const [resendTarget, setResendTarget] = useState<ResendTarget | null>(null);
  const [resending, setResending] = useState(false);
  const [toast, setToast] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  /**
   * WEL-141: open a completed W-9. The row id is `w9:<versionId>` — the dashboard
   * never carries a W-9's groupId, because that value derives the document's
   * PUBLIC (no-login) signing page. The server resolves the record, logs who
   * opened it, and returns a link that expires in five minutes, so no long-lived
   * URL to a tax ID is left sitting in a browser.
   */
  const openW9 = async (rowId: string): Promise<void> => {
    // Owner and vendor W-9 rows alike (owner-sync spec §6).
    const versionId = w9VersionIdFromRowId(rowId);
    if (!versionId) return;
    try {
      const { url } = await getW9DocumentUrl(versionId);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setToast({
        tone: 'danger',
        text:
          err instanceof ApiError && err.status === 409
            ? 'That W-9 has not been signed yet.'
            : 'Could not open that W-9.',
      });
    }
  };


  // Only a SUCCESS banner times out. An error here carries the server's
  // instruction for what to do instead ("That addendum is already fully signed
  // … send a new animal addendum"), and a refusal is read precisely when
  // somebody has no idea what their next move is. Six seconds is not long
  // enough to read it, decide, and act, and there is no way to get it back —
  // so a failure stays until it is dismissed.
  useEffect(() => {
    if (!toast || toast.tone !== 'success') return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  // Per-group outstanding (resendable) rows — drives the household "Nudge all" button.
  const groupOutstanding = useMemo(() => {
    const m = new Map<string, OutboundRequestRow[]>();
    for (const r of filtered) {
      if (!r.canResend) continue;
      const list = m.get(r.groupId);
      if (list) list.push(r);
      else m.set(r.groupId, [r]);
    }
    return m;
  }, [filtered]);

  const openResend = useCallback((targets: OutboundRequestRow[], label: string) => {
    if (targets.length === 0) return;
    const recent = targets.filter((t) => {
      if (!t.lastSentAt) return false;
      const ms = Date.now() - new Date(t.lastSentAt).getTime();
      return !Number.isNaN(ms) && ms >= 0 && ms < TWELVE_H_MS;
    });
    setResendTarget({ ids: targets.map((t) => t.id), label, recent });
  }, []);

  const confirmResend = useCallback(async () => {
    if (!resendTarget) return;
    setResending(true);
    try {
      const res = await resendRequests(resendTarget.ids);
      const firstFail = res.results.find((r) => !r.ok);
      setToast(
        res.failed === 0
          ? { tone: 'success', text: `Resent ${res.sent} message${res.sent === 1 ? '' : 's'}.` }
          : {
              tone: res.sent > 0 ? 'success' : 'danger',
              text: `Resent ${res.sent}; ${res.failed} failed${firstFail?.reason ? ` — ${firstFail.reason}` : ''}.`,
            },
      );
      setResendTarget(null);
      await load();
    } catch (err) {
      setToast({ tone: 'danger', text: err instanceof Error ? err.message : 'Resend failed' });
    } finally {
      setResending(false);
    }
  }, [resendTarget, load]);

  // --- WEL-46: multi-select + bulk cancel -----------------------------------
  // A row is selectable iff it's outstanding — same predicate single-row Resend uses.
  const selectableIds = useMemo(
    () => new Set(displayRows.filter((r) => r.canResend).map((r) => r.id)),
    [displayRows],
  );
  // Drop any selected ids that are no longer visible/selectable (filter changes).
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) if (selectableIds.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [selectableIds]);

  const selectedRows = useMemo(
    () => displayRows.filter((r) => selectedIds.has(r.id)),
    [displayRows, selectedIds],
  );
  const allSelectableSelected = selectableIds.size > 0 && selectedIds.size === selectableIds.size;

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => (prev.size === selectableIds.size ? new Set() : new Set(selectableIds)));
  }, [selectableIds]);

  // Per-group selectable ids — drives the grouped-view per-group select-all.
  const groupSelectable = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of displayRows) {
      if (!r.canResend) continue;
      const list = m.get(r.groupId);
      if (list) list.push(r.id);
      else m.set(r.groupId, [r.id]);
    }
    return m;
  }, [displayRows]);

  const toggleGroup = useCallback(
    (groupId: string) => {
      const ids = groupSelectable.get(groupId) ?? [];
      setSelectedIds((prev) => {
        const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allOn) for (const id of ids) next.delete(id);
        else for (const id of ids) next.add(id);
        return next;
      });
    },
    [groupSelectable],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const openCancel = useCallback((targets: OutboundRequestRow[], label: string) => {
    if (targets.length === 0) return;
    // A kind with its own Cancel must never go through the GENERIC one, and the
    // per-row button already enforces that. Bulk did not: selecting a live
    // move-out funds or animal addendum row and hitting "Cancel selected" sent
    // it to the generic endpoint, which knows only how to withdraw a DocuSeal
    // submission. On an intake-phase row that fails outright; on a signing-phase
    // row it is worse — the signing request is withdrawn while the household's
    // form links stay live and nobody is told, which is a half-cancelled request
    // that looks finished. They are dropped from the batch and named in the
    // modal instead.
    const eligible = targets.filter((t) => !CANCEL_RDA_KINDS.has(t.kind));
    const skippedDedicated = targets.length - eligible.length;
    if (eligible.length === 0) {
      setToast({
        tone: 'danger',
        text:
          skippedDedicated === 1
            ? 'That request has its own Cancel — use the Cancel button on its row.'
            : 'Those requests have their own Cancel — use the Cancel button on each row.',
      });
      return;
    }
    setCancelTarget({
      ids: eligible.map((t) => t.id),
      label,
      hasSigning: eligible.some((t) => withdrawsDocuSeal(t.kind)),
      skippedDedicated,
      canNotify: !eligible.some((t) => isW9Kind(t.kind)),
    });
  }, []);

  const confirmCancel = useCallback(
    async (notify: boolean, message: string) => {
      if (!cancelTarget) return;
      setCancelling(true);
      try {
        const trimmed = message.trim();
        const sendNotice = notify && cancelTarget.canNotify;
        const res = await cancelRequests(cancelTarget.ids, {
          notify: sendNotice,
          ...(trimmed && sendNotice ? { message: trimmed } : {}),
        });
        const firstFail = res.results.find((r) => !r.ok);
        setToast(
          res.failed === 0
            ? {
                tone: 'success',
                text: `Cancelled ${res.cancelled} request${res.cancelled === 1 ? '' : 's'}.`,
              }
            : {
                tone: res.cancelled > 0 ? 'success' : 'danger',
                text: `Cancelled ${res.cancelled}; ${res.failed} failed${firstFail?.reason ? ` — ${firstFail.reason}` : ''}.`,
              },
        );
        setCancelTarget(null);
        await load();
      } catch (err) {
        setToast({ tone: 'danger', text: err instanceof Error ? err.message : 'Cancel failed' });
      } finally {
        setCancelling(false);
      }
    },
    [cancelTarget, load],
  );

  // --- WEL-22 (req 5): revive a specific superseded designation -------------
  const [reviveTarget, setReviveTarget] = useState<OutboundRequestRow | null>(null);
  const [reviving, setReviving] = useState(false);

  const confirmRevive = useCallback(async () => {
    if (!reviveTarget || !reviveTarget.revivableInto) return;
    setReviving(true);
    try {
      await reviveDesignation(reviveTarget.revivableInto, reviveTarget.groupId);
      setToast({
        tone: 'success',
        text: `Set ${designeeNameOf(reviveTarget) || 'this designation'} as the household designation.`,
      });
      setReviveTarget(null);
      setOpenId(null);
      await load();
    } catch (err) {
      setToast({ tone: 'danger', text: err instanceof Error ? err.message : 'Revive failed' });
    } finally {
      setReviving(false);
    }
  }, [reviveTarget, load]);

  // --- WEL-48: void + restart the Move-Out Funds Direction intake ------------
  const [resendIntakeTarget, setResendIntakeTarget] = useState<OutboundRequestRow | null>(null);
  const [resendingIntake, setResendingIntake] = useState(false);

  const confirmResendIntake = useCallback(async () => {
    if (!resendIntakeTarget) return;
    setResendingIntake(true);
    try {
      const animal = isAnimalAddendumPaid(resendIntakeTarget.kind);
      const owner = isOwnerIntake(resendIntakeTarget.kind);
      if (owner) await resendOwnerIntake(resendIntakeTarget.groupId);
      else if (animal) await resendAnimalAddendumIntake(resendIntakeTarget.groupId);
      else await resendMoveoutFundsIntake(resendIntakeTarget.groupId);
      setToast({
        tone: 'success',
        text: owner
          ? 'Sent the owner intake form again.'
          : animal
            ? 'Voided the addendum and sent the animal form again.'
            : 'Voided the agreement and restarted the recipient-designation intake.',
      });
      setResendIntakeTarget(null);
      setOpenId(null);
      await load();
    } catch (err) {
      setToast({ tone: 'danger', text: serverMessage(err, 'Resend intake failed') });
    } finally {
      setResendingIntake(false);
    }
  }, [resendIntakeTarget, load]);

  // --- WEL-157: cancel the whole Move-Out Funds Direction request (RDA cancel) --
  const [cancelRdaTarget, setCancelRdaTarget] = useState<OutboundRequestRow | null>(null);
  const [cancellingRda, setCancellingRda] = useState(false);

  const confirmCancelRda = useCallback(async () => {
    if (!cancelRdaTarget) return;
    setCancellingRda(true);
    try {
      if (isOwnerIntake(cancelRdaTarget.kind)) {
        await cancelOwnerIntake(cancelRdaTarget.groupId);
        setToast({ tone: 'success', text: 'Owner intake cancelled. The form links no longer work.' });
        setCancelRdaTarget(null);
        setOpenId(null);
        await load();
        return;
      }
      const animal = isAnimalAddendumPaid(cancelRdaTarget.kind);
      const res = animal
        ? await cancelAnimalAddendum(cancelRdaTarget.groupId)
        : await cancelDepositAgreement(cancelRdaTarget.groupId);
      const what = animal ? 'animal addendum' : 'move-out funds request';
      setToast({
        tone: 'success',
        text: res.alreadyCancelled
          ? `This ${what} was already cancelled.`
          : `${what.charAt(0).toUpperCase()}${what.slice(1)} cancelled.${
              res.voidedSubmissionId ? ' The agreement out for signing was voided.' : ''
            }${res.recipientsEmailed > 0 ? ` ${res.recipientsEmailed} recipient${res.recipientsEmailed === 1 ? '' : 's'} notified.` : ''}`,
      });
      setCancelRdaTarget(null);
      setOpenId(null);
      await load();
    } catch (err) {
      setToast({ tone: 'danger', text: serverMessage(err, 'Cancel failed') });
    } finally {
      setCancellingRda(false);
    }
  }, [cancelRdaTarget, load]);

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Requests
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Every request WHP sends out — verifications, intake (designation / yard / animals), leases, and condition
          reports — with sent → opened → completed status. Resend any outstanding request (it resets their
          reminder schedule); use “Open in …” for system-specific actions.
        </p>
      </div>

      {/* Filter bar */}
      <div
        className="cluster"
        style={{ gap: 'var(--space-3)', marginBottom: 'var(--space-5)', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <label className="field" style={{ margin: 0 }}>
          <span className="label">Type</span>
          <select className="input" value={kindFilter} onChange={(e) => setKindFilter(e.target.value as KindFilterValue)}>
            {KIND_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="field" style={{ margin: 0 }}>
          <span className="label">Status</span>
          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            {STATUS_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="field" style={{ margin: 0, flex: '1 1 16rem' }}>
          <span className="label">Search</span>
          <input
            className="input"
            type="search"
            placeholder="Search anything — type, name, property, status… (regex ok)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {attentionCount > 0 && (
          <button
            type="button"
            className="btn"
            data-variant={statusFilter === 'needs_attention' ? 'primary' : 'secondary'}
            data-size="sm"
            onClick={() => setStatusFilter(statusFilter === 'needs_attention' ? 'all' : 'needs_attention')}
            style={{ marginBottom: '2px' }}
          >
            <span className="dot" data-tone="warning" data-pulse="true" style={{ marginRight: 'var(--space-2)' }} />
            {attentionCount} need{attentionCount === 1 ? 's' : ''} attention
          </button>
        )}
        <button
          type="button"
          className="btn"
          data-variant={groupView ? 'primary' : 'secondary'}
          data-size="sm"
          onClick={() => setGroupView((v) => !v)}
          style={{ marginBottom: '2px' }}
          title="Group rows by application"
        >
          {groupView ? 'Grouped by application' : 'Group by application'}
        </button>
      </div>

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      )}
      {toast && (
        <div
          className="alert"
          data-tone={toast.tone}
          // A failure is an assertive live region; a confirmation is not. Both
          // were announcing as 'status', so a screen-reader user could have the
          // refusal queued silently behind whatever was already speaking.
          role={toast.tone === 'danger' ? 'alert' : 'status'}
          style={{ marginBottom: 'var(--space-4)' }}
        >
          <div className="alert-body" style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
            <span>{toast.text}</span>
            <button
              type="button"
              className="btn"
              data-variant="ghost"
              data-size="sm"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
          No outbound requests yet.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* WEL-46: bulk selection action bar */}
          {selectedRows.length > 0 && (
            <div
              className="card"
              style={{
                marginBottom: 'var(--space-4)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                flexWrap: 'wrap',
                background: 'var(--bg-alt)',
              }}
            >
              <span style={{ fontWeight: 'var(--weight-medium)' }}>
                {selectedRows.length} selected
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                onClick={() => openResend(selectedRows, `${selectedRows.length} selected`)}
              >
                Resend selected ({selectedRows.length})
              </button>
              <button
                type="button"
                className="btn"
                data-variant="danger"
                data-size="sm"
                onClick={() => openCancel(selectedRows, `${selectedRows.length} selected`)}
              >
                Cancel selected ({selectedRows.length})
              </button>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                onClick={clearSelection}
              >
                Clear
              </button>
            </div>
          )}
          <div className="table-wrap">
            <table className="table" data-zebra="true">
              <thead>
                <tr>
                  <th scope="col" style={{ width: '2.5rem' }}>
                    <input
                      type="checkbox"
                      aria-label="Select all outstanding requests"
                      checked={allSelectableSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selectedIds.size > 0 && !allSelectableSelected;
                        }
                      }}
                      disabled={selectableIds.size === 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th scope="col">Type</th>
                  <th scope="col">Property</th>
                  <th scope="col">Unit</th>
                  <th scope="col">Applicants</th>
                  <th scope="col">Recipient</th>
                  <th scope="col">Sent</th>
                  <th scope="col">Opened</th>
                  <th scope="col" className="col-action">Status &amp; Details</th>
                  {/* WEL-135: who sent it. Deliberately last and narrow — initials
                      only, full name on hover. Blank when nobody clicked Send
                      (condition reports, verifications, auto-sent agreements). */}
                  <th scope="col" style={{ width: '3rem', textAlign: 'right' }} title="Who sent it">
                    Init
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r, i, arr) => {
                  const showHeader = groupView && (i === 0 || arr[i - 1]!.groupId !== r.groupId);
                  const roll = showHeader ? groupRollups.get(r.groupId) : undefined;
                  return (
                    <Fragment key={r.id}>
                      {showHeader && roll && (
                        <tr>
                          <td
                            colSpan={10}
                            style={{ background: 'var(--bg-muted, #f4f2ee)', fontWeight: 'var(--weight-medium)' }}
                          >
                            <div
                              className="cluster"
                              style={{ justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)' }}
                            >
                              <span className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                                {(() => {
                                  const gIds = groupSelectable.get(r.groupId) ?? [];
                                  if (gIds.length === 0) return null;
                                  const allOn = gIds.every((id) => selectedIds.has(id));
                                  const someOn = !allOn && gIds.some((id) => selectedIds.has(id));
                                  return (
                                    <input
                                      type="checkbox"
                                      aria-label={`Select all outstanding requests in ${roll.label}`}
                                      checked={allOn}
                                      ref={(el) => {
                                        if (el) el.indeterminate = someOn;
                                      }}
                                      onChange={() => toggleGroup(r.groupId)}
                                    />
                                  );
                                })()}
                                {roll.attention > 0 && <span className="dot" data-tone="warning" data-pulse="true" />}
                                <span>{roll.label}</span>
                                <span
                                  className="pill"
                                  data-tone={
                                    roll.completed === roll.total
                                      ? 'success'
                                      : roll.attention > 0
                                        ? 'warning'
                                        : 'accent'
                                  }
                                >
                                  {roll.completed}/{roll.total} done
                                </span>
                                {(() => {
                                  const sync = groupSync.get(r.groupId);
                                  return sync ? <AppfolioSyncChip sync={sync} onRetried={() => void load()} /> : null;
                                })()}
                              </span>
                              {(() => {
                                const out = groupOutstanding.get(r.groupId);
                                return out && out.length > 0 ? (
                                  <button
                                    type="button"
                                    className="btn"
                                    data-variant="secondary"
                                    data-size="sm"
                                    onClick={() => openResend(out, roll.label)}
                                    title={`Resend to ${out.length} outstanding recipient${out.length === 1 ? '' : 's'} in this household`}
                                  >
                                    Nudge {out.length} outstanding
                                  </button>
                                ) : null;
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr
                        onClick={() => setOpenId(r.id)}
                        style={{ cursor: 'pointer' }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open details for ${typeLabelOf(r)} — ${r.recipientName ?? r.recipientEmail ?? 'recipient'}`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenId(r.id);
                          }
                        }}
                      >
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.canResend ? (
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.recipientName ?? r.recipientEmail ?? 'this request'}`}
                          checked={selectedIds.has(r.id)}
                          onChange={() => toggleRow(r.id)}
                        />
                      ) : null}
                    </td>
                    <td style={{ fontWeight: 'var(--weight-medium)' }}>{typeLabelOf(r)}</td>
                    {/* WEL-64: Property — name/address; the once-per-group sync chip rides here. */}
                    <td style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)' }}>{r.property ?? r.groupLabel ?? '—'}</div>
                      {/* WEL-101: an RV is about a PRIOR residence — name it explicitly so it
                          can't be mistaken for the applied-for property above. */}
                      {r.kind === 'rental_verification' && r.priorAddress && (
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)' }}>
                          Verifying {r.priorAddress}
                        </div>
                      )}
                      {/* One sync chip per group; the grouped view shows it on the header row instead. */}
                      {!groupView && syncChipRowIds.has(r.id) && (() => {
                        const sync = groupSync.get(r.groupId);
                        return sync ? (
                          <div style={{ marginTop: 'var(--space-2)' }} onClick={(e) => e.stopPropagation()}>
                            <AppfolioSyncChip sync={sync} onRetried={() => void load()} />
                          </div>
                        ) : null;
                      })()}
                    </td>
                    {/* WEL-64: Unit — the WHP unit label/number. */}
                    <td style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)' }}>{r.unit ?? '—'}</div>
                    </td>
                    {/* WEL-64: Applicants — the group's applicant names (searchable). */}
                    <td style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)' }}>{r.applicants ?? '—'}</div>
                    </td>
                    <td>
                      <div style={{ minWidth: 0 }}>
                        <div>{r.recipientName ?? '—'}</div>
                        {r.recipientRole && (
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)' }}>{r.recipientRole}</div>
                        )}
                        {r.subjectLabel && (
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginTop: 'var(--space-1)' }}>
                            for {r.subjectLabel}
                          </div>
                        )}
                      </div>
                    </td>
                    <td title={fmtDateTime(r.sentAt)}>{fmtDate(r.sentAt)}</td>
                    <td title={fmtDateTime(r.openedAt)}>{fmtDate(r.openedAt)}</td>
                    {/* WEL-64: Status & Details — the status pill + every row action. */}
                    <td className="col-action" onClick={(e) => e.stopPropagation()}>
                      <span className="cluster" style={{ gap: 'var(--space-2)', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                          {r.needsAttention && <span className="dot" data-tone="warning" data-pulse="true" />}
                          {r.status === 'cancelled' ? (
                            <span className="pill" data-tone="neutral">Cancelled</span>
                          ) : (
                            <span className="pill" data-tone={r.tone}>{r.statusLabel}</span>
                          )}
                        </span>
                        {r.revivable && (
                          <button
                            type="button"
                            className="btn"
                            data-variant="secondary"
                            data-size="sm"
                            onClick={() => setReviveTarget(r)}
                            title={`Make ${designeeNameOf(r) || 'this'} the household designation`}
                          >
                            Use this designation
                          </button>
                        )}
                        {r.canResend && (
                          <button
                            type="button"
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            onClick={() => openResend([r], r.recipientName ?? r.recipientEmail ?? 'this recipient')}
                            title={resendHint(r)}
                          >
                            Resend{r.manualSends && r.manualSends.length > 0 ? ` ·${r.manualSends.length}` : ''}
                          </button>
                        )}
                        {offersGenericCancel(r) && (
                          <button
                            type="button"
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            onClick={() => openCancel([r], r.recipientName ?? r.recipientEmail ?? 'this recipient')}
                            title="Cancel/withdraw this request"
                          >
                            Cancel
                          </button>
                        )}
                        {cancelRdaRowIds.has(r.id) && (
                          <button
                            type="button"
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            onClick={() => setCancelRdaTarget(r)}
                            title={
                              isAnimalAddendumPaid(r.kind)
                                ? "Cancel this animal addendum (voids the agreement if it's out for signing)"
                                : "Cancel this move-out funds request (voids the agreement if it's out for signing)"
                            }
                          >
                            Cancel
                          </button>
                        )}
                        {resendIntakeRowIds.has(r.id) && (
                          <button
                            type="button"
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            onClick={() => setResendIntakeTarget(r)}
                            title={
                              isAnimalAddendumPaid(r.kind)
                                ? 'Void this addendum and send the animal form again'
                                : 'Void this agreement and restart the recipient-designation intake'
                            }
                          >
                            Resend intake
                          </button>
                        )}
                        {r.appfolioApplicationUrl && (
                          <a
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            data-icon-only
                            href={r.appfolioApplicationUrl}
                            target="_blank"
                            rel="noreferrer"
                            title="Open AppFolio application"
                            aria-label="Open AppFolio application"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        )}
                        {r.docusealUrl && (
                          <a
                            className="btn"
                            data-variant="ghost"
                            data-size="sm"
                            data-icon-only
                            href={r.docusealUrl}
                            target="_blank"
                            rel="noreferrer"
                            title="View in DocuSeal"
                            aria-label="View in DocuSeal"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        )}
                        {isW9Kind(r.kind) && r.status === 'completed' && (
                          <button
                            type="button"
                            className="btn"
                            data-variant="secondary"
                            data-size="sm"
                            title="Open the signed W-9 (link expires in 5 minutes)"
                            onClick={() => void openW9(r.id)}
                          >
                            View W-9
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          onClick={() => setOpenId(r.id)}
                        >
                          Details
                        </button>
                      </span>
                    </td>
                    {/* WEL-135: initials of whoever sent it, full name on hover. */}
                    <td
                      style={{
                        textAlign: 'right',
                        color: 'var(--fg-muted)',
                        fontSize: 'var(--text-xs)',
                        whiteSpace: 'nowrap',
                      }}
                      {...(r.sentBy ? { title: `Sent by ${r.sentBy}` } : {})}
                    >
                      {initialsOf(r.sentBy)}
                    </td>
                  </tr>
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: 'var(--space-6)' }}>
                      No requests match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)', marginTop: 'var(--space-3)' }}>
            Showing {filtered.length} of {rows.length} request{rows.length === 1 ? '' : 's'}.
          </p>
        </>
      )}

      <RequestDetailModal
        row={openRow}
        open={openRow !== null}
        onClose={() => setOpenId(null)}
        onResend={(r) => {
          setOpenId(null);
          openResend([r], r.recipientName ?? r.recipientEmail ?? 'this recipient');
        }}
        onRevive={(r) => setReviveTarget(r)}
        onChanged={() => void load()}
      />
      <ResendConfirmModal
        target={resendTarget}
        busy={resending}
        onConfirm={() => void confirmResend()}
        onClose={() => setResendTarget(null)}
      />
      <CancelConfirmModal
        target={cancelTarget}
        busy={cancelling}
        onConfirm={(notify, message) => void confirmCancel(notify, message)}
        onClose={() => setCancelTarget(null)}
      />
      <ReviveConfirmModal
        row={reviveTarget}
        busy={reviving}
        onConfirm={() => void confirmRevive()}
        onClose={() => setReviveTarget(null)}
      />
      <ResendIntakeConfirmModal
        row={resendIntakeTarget}
        busy={resendingIntake}
        onConfirm={() => void confirmResendIntake()}
        onClose={() => setResendIntakeTarget(null)}
      />
      <CancelRdaConfirmModal
        row={cancelRdaTarget}
        busy={cancellingRda}
        onConfirm={() => void confirmCancelRda()}
        onClose={() => setCancelRdaTarget(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer — timeline + collected data + deep link.
// ---------------------------------------------------------------------------

function RequestDetailModal({
  row,
  open,
  onClose,
  onResend,
  onRevive,
  onChanged,
}: {
  row: OutboundRequestRow | null;
  open: boolean;
  onClose: () => void;
  onResend: (row: OutboundRequestRow) => void;
  onRevive: (row: OutboundRequestRow) => void;
  /** Refetch the rows after an in-drawer mutation (e.g. a signer email swap) —
   *  openRow derives from rows, so the open drawer refreshes in place. */
  onChanged?: () => void;
}): React.JSX.Element | null {
  const containerRef = useFocusTrap(open);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEscapeKey(onClose, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open || !row) return null;

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="req-modal-title" className="modal" data-size="lg">
        <div className="modal-header">
          <div className="stack-sm" style={{ minWidth: 0 }}>
            <h2 id="req-modal-title" className="modal-title">{typeLabelOf(row)}</h2>
            <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              {row.recipientName ?? '—'}
              {row.recipientEmail ? ` · ${row.recipientEmail}` : ''}
            </div>
          </div>
          <span className="pill" data-tone={row.tone} style={{ alignSelf: 'flex-start' }}>{row.statusLabel}</span>
          <button
            ref={closeRef}
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            data-icon-only
            aria-label="Close"
            onClick={onClose}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div className="stack">
            {/* Group + timeline */}
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {row.groupLabel && <KeyVal label="Property" value={row.groupLabel} />}
              {row.subjectLabel && <KeyVal label="Applicant" value={row.subjectLabel} />}
              {row.recipientRole && <KeyVal label="Role" value={row.recipientRole} />}
              {row.recipientEmail && <KeyVal label="Email" value={row.recipientEmail} />}
              {/* WEL-50: link the row to the applicant's AppFolio application. */}
              {row.appfolioApplicationUrl && (
                <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', minWidth: '6rem' }}>
                    Application
                  </span>
                  <a
                    href={row.appfolioApplicationUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    Open in AppFolio ↗
                  </a>
                </div>
              )}
              <Timeline row={row} />
              {row.manualSends && row.manualSends.length > 0 && (
                <ManualSendHistory entries={row.manualSends} />
              )}
            </div>

            {/* Collected data */}
            <DataSection row={row} {...(onChanged ? { onChanged } : {})} />

            {/* WEL-57: full per-party transaction history + what's still to happen. */}
            <ActivityHistory row={row} />
          </div>
        </div>
        <div className="modal-footer">
          {/* Current-state document download — every signature-backed request's
              detail offers its document (executed / partially-signed / generated). */}
          {DOCUMENT_KINDS.has(row.kind) && (
            <CurrentDocumentDownload
              groupId={row.groupId}
              documentType={row.kind as CurrentDocumentType}
              label="Download document"
              size="md"
              unavailableNote="No document yet for this request."
            />
          )}
          {row.revivable && (
            <button
              type="button"
              className="btn"
              data-variant="primary"
              onClick={() => onRevive(row)}
              title={`Make ${designeeNameOf(row) || 'this'} the household designation`}
            >
              Use this designation
            </button>
          )}
          {row.canResend && (
            <button
              type="button"
              className="btn"
              data-variant={row.revivable ? 'secondary' : 'primary'}
              onClick={() => onResend(row)}
              title={resendHint(row)}
            >
              Resend
            </button>
          )}
          {row.deepLinkPath && (
            <Link className="btn" data-variant="secondary" to={row.deepLinkPath} onClick={onClose}>
              Open in {deepLinkLabel(row.deepLinkPath)} →
            </Link>
          )}
          {/* WEL-50: jump to the applicant's AppFolio rental application. */}
          {row.appfolioApplicationUrl && (
            <a
              className="btn"
              data-variant="secondary"
              href={row.appfolioApplicationUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open in AppFolio ↗
            </a>
          )}
          {row.docusealUrl && (
            <a
              className="btn"
              data-variant="secondary"
              href={row.docusealUrl}
              target="_blank"
              rel="noreferrer"
            >
              View in DocuSeal ↗
            </a>
          )}
          <button type="button" className="btn" data-variant="secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

function deepLinkLabel(path: string): string {
  if (path === '/verifications') return 'Verifications';
  if (path === '/condition-reports') return 'Condition reports';
  if (path === '/applications') return 'Applications';
  return path;
}

// ---------------------------------------------------------------------------
// Resend confirm — 12h anti-spam guard.
// ---------------------------------------------------------------------------

function ResendConfirmModal({
  target,
  busy,
  onConfirm,
  onClose,
}: {
  target: ResendTarget | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.JSX.Element | null {
  const open = target !== null;
  const containerRef = useFocusTrap(open);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEscapeKey(() => { if (!busy) onClose(); }, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  if (!open || !target) return null;

  const count = target.ids.length;
  const recent = target.recent;
  const mostRecent = recent.map((r) => r.lastSentAt!).sort().at(-1);

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="resend-title" className="modal">
        <div className="modal-header">
          <h2 id="resend-title" className="modal-title">
            {count === 1 ? 'Resend this request?' : `Resend ${count} requests?`}
          </h2>
        </div>
        <div className="modal-body">
          <div className="stack">
            <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              {count === 1 ? (
                <>
                  This re-sends the email to <strong>{target.label}</strong> and resets their reminder schedule —
                  the next automatic reminder will count from now.
                </>
              ) : (
                <>
                  This re-sends to <strong>{count}</strong> outstanding recipients in <strong>{target.label}</strong>{' '}
                  and resets each of their reminder schedules.
                </>
              )}
            </p>
            {recent.length > 0 && (
              <div className="alert" data-tone="warning" role="alert">
                <div className="alert-body">
                  <div className="alert-title">
                    {recent.length === count
                      ? count === 1
                        ? 'They were emailed very recently'
                        : 'These were all emailed very recently'
                      : `${recent.length} of these were emailed within the last 12 hours`}
                  </div>
                  <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}>
                    {mostRecent ? `Most recent: ${relativeAge(mostRecent)}. ` : ''}
                    Sending again so soon can feel like spam — only resend if they asked for it (e.g. they lost the
                    email). Are you sure?
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn"
            data-variant={recent.length > 0 ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Sending…' : count === 1 ? 'Resend now' : `Resend all ${count}`}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

// ---------------------------------------------------------------------------
// Cancel confirm — withdraw a request (also pulls the DocuSeal request) — WEL-46.
// ---------------------------------------------------------------------------

function CancelConfirmModal({
  target,
  busy,
  onConfirm,
  onClose,
}: {
  target: CancelTarget | null;
  busy: boolean;
  onConfirm: (notify: boolean, message: string) => void;
  onClose: () => void;
}): React.JSX.Element | null {
  const open = target !== null;
  const containerRef = useFocusTrap(open);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState('');

  // Reset the notify toggle (default OFF) + message each time the modal opens.
  useEffect(() => {
    if (open) {
      setNotify(false);
      setMessage('');
      confirmRef.current?.focus();
    }
  }, [open]);

  useEscapeKey(() => { if (!busy) onClose(); }, open);
  useInertBackground(open);

  if (!open || !target) return null;

  const count = target.ids.length;

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="cancel-title" className="modal">
        <div className="modal-header">
          <h2 id="cancel-title" className="modal-title">
            {count === 1 ? 'Cancel this request?' : `Cancel ${count} requests?`}
          </h2>
        </div>
        <div className="modal-body">
          <div className="stack">
            <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              {count === 1 ? (
                <>
                  This cancels the request to <strong>{target.label}</strong> and stops its reminders.
                </>
              ) : (
                <>
                  This cancels <strong>{count}</strong> selected requests and stops their reminders.
                </>
              )}
            </p>
            {target.skippedDedicated > 0 && (
              <div className="alert" data-tone="warning" role="alert">
                <div className="alert-body">
                  <div className="alert-title">
                    {target.skippedDedicated === 1
                      ? 'One selected request is not included'
                      : `${target.skippedDedicated} selected requests are not included`}
                  </div>
                  <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}>
                    Move-out funds and animal addendum requests have to be cancelled from their own
                    row, so that both the household's form and any signing request close together.
                    Cancel {target.skippedDedicated === 1 ? 'it' : 'them'} separately.
                  </p>
                </div>
              </div>
            )}
            {target.hasSigning && (
              <div className="alert" data-tone="warning" role="alert">
                <div className="alert-body">
                  <div className="alert-title">
                    {count === 1 ? 'This is a signing request' : 'Some of these are signing requests'}
                  </div>
                  <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}>
                    Canceling will also <strong>withdraw the DocuSeal signing request</strong> — any
                    in-progress signatures will be voided and recipients can no longer sign.
                  </p>
                </div>
              </div>
            )}
            {target.canNotify && (
              <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={notify}
                  disabled={busy}
                  onChange={(e) => setNotify(e.target.checked)}
                />
                Notify recipients that the request was canceled
              </label>
            )}
            {notify && target.canNotify && (
              <label className="field" style={{ margin: 0 }}>
                <span className="label">Message (optional)</span>
                <textarea
                  className="input"
                  rows={3}
                  value={message}
                  disabled={busy}
                  placeholder="Add a short note to include in the cancellation email…"
                  onChange={(e) => setMessage(e.target.value)}
                />
              </label>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={onClose} disabled={busy}>
            Keep {count === 1 ? 'request' : 'requests'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn"
            data-variant="danger"
            onClick={() => onConfirm(notify, message)}
            disabled={busy}
          >
            {busy ? 'Canceling…' : count === 1 ? 'Cancel request' : `Cancel ${count} requests`}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

// ---------------------------------------------------------------------------
// Revive confirm — promote a superseded designation to the household's (WEL-22).
// ---------------------------------------------------------------------------

function ReviveConfirmModal({
  row,
  busy,
  onConfirm,
  onClose,
}: {
  row: OutboundRequestRow | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.JSX.Element | null {
  const open = row !== null;
  const containerRef = useFocusTrap(open);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEscapeKey(() => { if (!busy) onClose(); }, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  if (!open || !row) return null;

  const designee = designeeNameOf(row);
  const submittedBy = row.recipientName ?? 'a household member';

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="revive-title" className="modal">
        <div className="modal-header">
          <h2 id="revive-title" className="modal-title">Use this designation?</h2>
        </div>
        <div className="modal-body">
          <div className="stack">
            <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              Make <strong>{designee || 'this designee'}</strong>
              {' '}— submitted by <strong>{submittedBy}</strong> before these applications were merged —
              {' '}the household&rsquo;s authoritative resident designation. This satisfies the lease&rsquo;s
              designation requirement.
            </p>
            <p style={{ margin: 0, color: 'var(--fg-subtle)', fontSize: 'var(--text-sm)' }}>
              You can change it later — revive a different one, or type a designee in the lease builder
              (which always wins).
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn"
            data-variant="primary"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Setting…' : 'Use this designation'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

// ---------------------------------------------------------------------------
// Resend-intake confirm — void the open agreement and restart the household's
// form from scratch (WEL-48 move-out funds; WEL-153 animal addendum).
//
// The two documents ask the household for different things, so the copy pivots
// on the row's kind: a mis-named confirm is how an operator voids the wrong
// signing request.
// ---------------------------------------------------------------------------

function ResendIntakeConfirmModal({
  row,
  busy,
  onConfirm,
  onClose,
}: {
  row: OutboundRequestRow | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.JSX.Element | null {
  const open = row !== null;
  const containerRef = useFocusTrap(open);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEscapeKey(() => { if (!busy) onClose(); }, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  if (!open || !row) return null;

  const label = row.groupLabel ?? row.property ?? 'this tenancy';
  const animal = isAnimalAddendumPaid(row.kind);
  const owner = isOwnerIntake(row.kind);

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="resend-intake-title" className="modal">
        <div className="modal-header">
          <h2 id="resend-intake-title" className="modal-title">
            {owner ? 'Send the owner intake again?' : animal ? 'Send the animal form again?' : 'Restart the recipient intake?'}
          </h2>
        </div>
        <div className="modal-body">
          <div className="stack">
            <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              {animal ? (
                <>
                  This voids the open animal addendum for <strong>{label}</strong> and re-sends the
                  animal form to all current residents and co-signers. Use this when the animal
                  details need to be entered again.
                </>
              ) : (
                <>
                  This voids the open Move-Out Funds Direction Agreement for <strong>{label}</strong>{' '}
                  and re-sends the recipient-designation intake to all current residents and
                  co-signers. Use this when the household needs to re-pick who receives the funds.
                </>
              )}
            </p>
            <div className="alert" data-tone="warning" role="alert">
              <div className="alert-body">
                <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                  Any in-progress signatures on the current {animal ? 'addendum' : 'agreement'} will
                  be <strong>voided</strong>. A fresh form goes out, and the first response locks the
                  new {animal ? 'animal details' : 'selection'}.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={onClose} disabled={busy}>
            {animal ? 'Keep addendum' : 'Keep agreement'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn"
            data-variant="danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Restarting…' : animal ? 'Void & resend form' : 'Void & restart intake'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

// ---------------------------------------------------------------------------
// Cancel the whole request through its own dedicated endpoint — WEL-157
// (move-out funds) and WEL-153 (animal addendum). Both close BOTH phases at
// once: the household form's links AND a signing request that is already out.
// The copy pivots on the kind, for the same reason the resend modal's does.
// ---------------------------------------------------------------------------

function CancelRdaConfirmModal({
  row,
  busy,
  onConfirm,
  onClose,
}: {
  row: OutboundRequestRow | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.JSX.Element | null {
  const open = row !== null;
  const containerRef = useFocusTrap(open);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEscapeKey(() => { if (!busy) onClose(); }, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  if (!open || !row) return null;

  const label = row.groupLabel ?? row.property ?? 'this tenancy';
  // The intake-phase row has no signing submission; the agreement row does.
  const agreementOut = !row.id.endsWith(':intake');
  const animal = isAnimalAddendumPaid(row.kind);
  const owner = isOwnerIntake(row.kind);

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div ref={containerRef} role="dialog" aria-modal="true" aria-labelledby="cancel-rda-title" className="modal">
        <div className="modal-header">
          <h2 id="cancel-rda-title" className="modal-title">
            {owner ? 'Cancel this owner intake?' : animal ? 'Cancel this animal addendum?' : 'Cancel this move-out funds request?'}
          </h2>
        </div>
        <div className="modal-body">
          <div className="stack">
            <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              {animal ? (
                <>
                  This cancels the animal addendum for <strong>{label}</strong>: the animal form
                  stops working, reminders stop, and everyone in the household is emailed that there
                  is nothing further to do. The tenancy stays sendable — a later send starts fresh.
                </>
              ) : (
                <>
                  This cancels the Move-Out Funds Direction request for <strong>{label}</strong>: the
                  recipient links stop working, reminders stop, and each recipient is emailed that
                  there is nothing further to do. The tenancy stays sendable — a later send starts
                  fresh.
                </>
              )}
            </p>
            {agreementOut && (
              <div className="alert" data-tone="warning" role="alert">
                <div className="alert-body">
                  <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                    The {animal ? 'addendum' : 'agreement'} is out for signing. Any signatures
                    already collected on it will be <strong>voided</strong>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" data-variant="secondary" onClick={onClose} disabled={busy}>
            {animal ? 'Keep addendum' : 'Keep request'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn"
            data-variant="danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Cancelling…' : animal ? 'Cancel addendum' : 'Cancel request'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

/** Read-only list of who manually resent this request, and when. */
function ManualSendHistory({ entries }: { entries: ManualSendEntry[] }): React.JSX.Element {
  return (
    <div className="stack-sm">
      <SectionTitle>Manual resends</SectionTitle>
      <div className="stack-sm" style={{ gap: 'var(--space-1)' }}>
        {entries
          .slice()
          .reverse()
          .map((e, i) => (
            <div key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }} title={e.at}>
              {fmtDateTime(e.at)} · {relativeAge(e.at)} — by {e.by}
            </div>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WEL-57: the full per-party transaction history at the bottom of the modal.
// For every requested party: each email-view + page-open (with timestamps and
// repeat counts), reminders, and completion — plus, up top, the "still to
// happen" checklist. Miles: "a full detailed transaction history of actions of
// the requested parties, including what is still to happen."
// ---------------------------------------------------------------------------

const EVENT_LABEL: Record<RequestEventKind, string> = {
  sent: 'Request emailed',
  reminded: 'Reminder sent',
  email_opened: 'Opened the email',
  page_opened: 'Opened the request page',
  completed: 'Completed',
  signed: 'Signed',
  responded: 'Responded',
  declined: 'Declined',
  bounced: 'Email bounced',
  cancelled: 'Cancelled',
  next_steps_sent: 'Next Steps sent',
};

const EVENT_TONE: Record<RequestEventKind, string> = {
  sent: 'info',
  reminded: 'warning',
  email_opened: 'accent',
  page_opened: 'accent',
  completed: 'success',
  signed: 'success',
  responded: 'success',
  declined: 'danger',
  bounced: 'danger',
  cancelled: 'neutral',
  next_steps_sent: 'info',
};

/** A short summary line for one party: "Viewed email 3×, opened page 2×, signed". */
function recipientSummary(r: RecipientActivity): string {
  const parts: string[] = [];
  if (r.emailOpenCount > 0)
    parts.push(`Viewed email ${r.emailOpenCount}×`);
  if (r.pageOpenCount > 0)
    parts.push(`Opened page ${r.pageOpenCount}×`);
  if (r.completedAt) parts.push('Completed');
  else if (r.pending) parts.push(r.pending);
  return parts.join(' · ') || 'No activity yet';
}

function ActivityHistory({ row }: { row: OutboundRequestRow }): React.JSX.Element | null {
  const activity = activityOf(row);
  if (!activity) return null;
  const { recipients, pending } = activity;
  // Hide an entirely-empty history (no events on any party AND nothing pending).
  const anyEvents = recipients.some((r) => r.events.length > 0);
  if (!anyEvents && pending.length === 0) return null;

  return (
    <div className="stack">
      <SectionTitle>Activity &amp; history</SectionTitle>

      {/* What's still to happen (the checklist). */}
      {pending.length > 0 && (
        <div className="alert" data-tone="info" role="status">
          <div className="alert-body">
            <div className="alert-title">Still to happen</div>
            <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
              {pending.map((p, i) => (
                <li key={i} style={{ fontSize: 'var(--text-sm)' }}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Per-party transaction history. */}
      {recipients.map((r) => (
        <div
          key={r.recipient}
          className="card"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
        >
          <div className="cluster" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-2)' }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 'var(--weight-medium)' }}>
                {partyLabel(r)}
              </span>
              {r.role && r.name && (
                <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
                  {' · '}{roleLabel(r.role)}
                </span>
              )}
              {r.email && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>{r.email}</div>
              )}
            </div>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', textAlign: 'right' }}>
              {recipientSummary(r)}
            </span>
          </div>
          {r.events.length > 0 ? (
            <ol
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-1)',
              }}
            >
              {r.events.map((e, i) => (
                <ActivityRow key={`${e.kind}-${e.at}-${i}`} ev={e} />
              ))}
            </ol>
          ) : (
            <EmptyData label="No recorded activity yet." />
          )}
        </div>
      ))}
    </div>
  );
}

/** One event line: a tone dot + label + precise timestamp (relative on the side). */
function ActivityRow({ ev }: { ev: ActivityEvent }): React.JSX.Element {
  return (
    <li
      className="cluster"
      style={{ gap: 'var(--space-2)', alignItems: 'baseline', fontSize: 'var(--text-sm)' }}
      title={ev.at}
    >
      <span className="dot" data-tone={EVENT_TONE[ev.kind]} style={{ flex: '0 0 auto' }} />
      <span style={{ fontWeight: 'var(--weight-medium)' }}>{EVENT_LABEL[ev.kind]}</span>
      <span style={{ color: 'var(--fg-muted)' }}>{fmtDateTime(ev.at)}</span>
      <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
        · {relativeAge(ev.at)}
      </span>
      {ev.synthesized && (
        <span
          style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}
          title="Inferred from the recorded status — predates per-event tracking."
        >
          (inferred)
        </span>
      )}
    </li>
  );
}

function Timeline({ row }: { row: OutboundRequestRow }): React.JSX.Element {
  const steps: Array<{ label: string; at: string | undefined; done: boolean }> = [
    { label: 'Sent', at: row.sentAt, done: !!row.sentAt },
    { label: 'Opened', at: row.openedAt, done: !!row.openedAt },
    { label: 'Completed', at: row.completedAt, done: !!row.completedAt },
  ];
  return (
    <div className="cluster" style={{ gap: 'var(--space-5)', flexWrap: 'wrap' }}>
      {steps.map((s) => (
        <div key={s.label} style={{ minWidth: '7rem' }}>
          <div className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
            <span className="dot" data-tone={s.done ? 'success' : 'neutral'} />
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' }}>{s.label}</span>
          </div>
          {/* WEL-47/50: full date + time (raw ISO on hover) for the sent / viewed / completed history. */}
          <div
            style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginLeft: 'var(--space-4)' }}
            title={s.at}
          >
            {s.at ? `${fmtDateTime(s.at)} · ${relativeAge(s.at)}` : '—'}
          </div>
        </div>
      ))}
      {row.lastReminderAt && (
        <div style={{ minWidth: '7rem' }}>
          <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' }}>
            Reminders{row.reminderCount ? ` (${row.reminderCount})` : ''}
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }} title={row.lastReminderAt}>
            last {fmtDateTime(row.lastReminderAt)} · {relativeAge(row.lastReminderAt)}
          </div>
        </div>
      )}
    </div>
  );
}

/** Render the collected payload per kind (best-effort, generic fallback). */
function DataSection({
  row,
  onChanged,
}: {
  row: OutboundRequestRow;
  /** Refetch rows after an in-roster mutation (signer email swap). */
  onChanged?: () => void;
}): React.JSX.Element | null {
  const data = row.data;
  if (data == null || typeof data !== 'object') return null;

  // WEL-80: the animal-intake payload is wrapped as `{ animals: [...] }` (so the
  // server's activity-history merge — `{ ...data, activity }` — doesn't mangle
  // the list into a numeric-keyed object). Read the list off `data.animals`,
  // mirroring how RV/designation read their fields off the data object.
  if (row.kind === 'intake_animals') {
    const animals = (data as { animals?: unknown }).animals;
    const list = Array.isArray(animals) ? (animals as Array<Record<string, unknown>>) : [];
    // WEL-54: an empty animal list is ambiguous on its own. Once the section is
    // completed, an empty list is the applicant's affirmative "we have no
    // animals" answer — say so (animalsEmptyLabel), rather than the bug-looking
    // "not recorded yet" (only correct while the request is still outstanding).
    if (list.length === 0) return <EmptyData label={animalsEmptyLabel(row.status)} />;
    return (
      <div className="stack-sm">
        <SectionTitle>Animals</SectionTitle>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Type</th>
                <th scope="col">Breed</th>
                <th scope="col">Details</th>
                <th scope="col">License</th>
              </tr>
            </thead>
            <tbody>
              {list.map((a, i) => {
                // WEL-173: an 'other' animal with a kind reads as the kind
                // ("Bird — cockatiel", "Fish"); one without keeps its old label.
                const type =
                  str(a.type) === 'other' && str(a.otherKind)
                    ? animalKindLabel({
                        type: 'other',
                        otherKind: str(a.otherKind),
                        otherDescription: str(a.otherDescription),
                      })
                    : str(a.type) === 'other' && str(a.otherDescription)
                      ? str(a.otherDescription)
                      : str(a.type);
                const details = [
                  str(a.color),
                  a.age != null
                    ? `${a.age}${isAnimalAgeUnit(a.ageUnit) && a.ageUnit === 'months' ? 'mo' : 'y'}`
                    : '',
                  str(a.gender),
                  a.weightLbs != null ? `${a.weightLbs}lb` : '',
                  a.spayedNeutered === true ? 'spayed/neutered' : '',
                  a.tankGallons != null ? `${a.tankGallons} gal tank` : '',
                ]
                  .filter(Boolean)
                  .join(' · ');
                const licenseAsked = asksAnimalField(
                  animalQuestionKind({ type: str(a.type), otherKind: str(a.otherKind) }),
                  'licenseNumber',
                );
                const license = str(a.licenseNumber)
                  ? str(a.licenseNumber)
                  : a.licenseMissingAck === true && licenseAsked
                    ? 'None (acknowledged)'
                    : '';
                return (
                  <tr key={String(a.id ?? i)}>
                    <td>{str(a.name) || '—'}</td>
                    <td>{type || '—'}</td>
                    <td>{str(a.breed) || '—'}</td>
                    <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                      {details || '—'}
                    </td>
                    <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                      {license || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (row.kind === 'rental_verification') {
    const d = data as {
      claimed?: { address?: Record<string, unknown>; current?: boolean; residedFrom?: string; residedTo?: string; monthlyRent?: number };
      landlord?: { name?: string; email?: string };
      applicantNames?: string[];
      response?: Record<string, unknown>;
      redFlags?: string[];
      signals?: string[];
      verdict?: string;
      skipReason?: string;
      cancelReason?: string;
    };
    const resp = (d.response ?? {}) as Record<string, unknown>;
    const confirmed = (resp.confirmed ?? {}) as Record<string, unknown>;
    const screening = (resp.screening ?? {}) as Record<string, unknown>;
    const respondent = (resp.respondent ?? {}) as Record<string, unknown>;
    const addr = d.claimed?.address as Record<string, unknown> | undefined;
    const period = [d.claimed?.residedFrom, d.claimed?.residedTo].filter(Boolean).join(' – ');
    return (
      <div className="stack">
        {d.redFlags && d.redFlags.length > 0 && (
          <div className="alert" data-tone="danger">
            <div className="alert-body">
              <div className="alert-title">Red flags</div>
              <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                {d.redFlags.map((f, i) => <li key={i} style={{ fontSize: 'var(--text-sm)' }}>{f}</li>)}
              </ul>
            </div>
          </div>
        )}
        <div className="stack-sm">
          <SectionTitle>Tenancy</SectionTitle>
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {d.applicantNames && d.applicantNames.length > 0 && (
              <KeyVal label="Applicant(s)" value={d.applicantNames.join(', ')} />
            )}
            {addr?.line1 != null && <KeyVal label="Address" value={String(addr.line1)} />}
            {period && <KeyVal label="Resided" value={period} />}
            {d.claimed?.monthlyRent != null && <KeyVal label="Claimed rent" value={`$${d.claimed.monthlyRent}`} />}
            {d.landlord?.name && <KeyVal label="Landlord" value={d.landlord.name} />}
            {d.landlord?.email && <KeyVal label="Landlord email" value={d.landlord.email} />}
          </div>
        </div>
        <div className="stack-sm">
          <SectionTitle>Landlord response</SectionTitle>
          {Object.keys(resp).length === 0 ? (
            <EmptyData label="No landlord response yet." />
          ) : (
            <div className="stack-sm">
              <KeyValGrid obj={resp} emptyLabel="—" />
              {Object.keys(confirmed).length > 0 && (
                <>
                  <SubLabel>Tenancy confirmed</SubLabel>
                  <KeyValGrid obj={confirmed} emptyLabel="—" />
                </>
              )}
              {Object.keys(screening).length > 0 && (
                <>
                  <SubLabel>Screening</SubLabel>
                  <KeyValGrid obj={screening} emptyLabel="—" />
                </>
              )}
              {Object.keys(respondent).length > 0 && (
                <>
                  <SubLabel>Respondent</SubLabel>
                  <KeyValGrid obj={respondent} emptyLabel="—" />
                </>
              )}
            </div>
          )}
        </div>
        {d.signals && d.signals.length > 0 && <KeyVal label="Signals" value={d.signals.join(', ')} />}
        {d.verdict && <KeyVal label="Staff verdict" value={d.verdict} />}
        {d.skipReason && <KeyVal label="Skip reason" value={d.skipReason} />}
        {d.cancelReason && <KeyVal label="Cancel note" value={d.cancelReason} />}
      </div>
    );
  }

  // Signing docs (lease / addendum / condition report / EPMA / move-out funds) →
  // the FULL signer roster + the document summary (every party's status, regardless
  // of which signer row was opened). WEL-92: EPMA reaches this via isSigningKind so
  // an owner document shows its owner→agent→broker roster, not the generic grid.
  if (isSigningKind(row.kind)) {
    const d = data as { leaseSummary?: Record<string, unknown>; signers?: SignerRow[] };
    const detailLabel =
      row.kind === 'condition_report'
        ? 'Report details'
        : row.kind === 'property_management_agreement' ||
            row.kind === 'leaseholder_change' ||
            row.kind === 'leaseholder_removal'
          ? 'Agreement details'
          : row.kind === 'animal_addendum' || row.kind === 'animal_addendum_paid'
            ? 'Addendum details'
            : 'Lease details';
    // Change-email is offered per not-yet-signed signer, but only while the
    // request is live: the PATCH acts on the group's NEWEST record for this
    // documentType, so a withdrawn/declined/expired request must not offer it.
    const rosterLive =
      row.status !== 'cancelled' && row.status !== 'declined' && row.status !== 'expired';
    return (
      <div className="stack">
        {d.signers && d.signers.length > 0 && (
          <SignersTable
            signers={d.signers}
            {...(rosterLive
              ? {
                  swap: {
                    groupId: row.groupId,
                    documentType: row.kind as CurrentDocumentType,
                  },
                }
              : {})}
            {...(onChanged ? { onChanged } : {})}
          />
        )}
        <div className="stack-sm">
          <SectionTitle>{detailLabel}</SectionTitle>
          <KeyValGrid obj={d.leaseSummary ?? {}} emptyLabel="No details collected yet." />
        </div>
      </div>
    );
  }

  // Intake designation / yard → flat object of collected fields.
  return (
    <div className="stack-sm">
      <SectionTitle>Details</SectionTitle>
      <KeyValGrid obj={data as Record<string, unknown>} emptyLabel="No data collected yet." />
    </div>
  );
}

/** One signer as projected on a signing row's `data.signers` (mirror of the API SignerSummary). */
type SignerRow = {
  role: string;
  name?: string;
  email?: string;
  order?: number;
  status: string;
  statusLabel: string;
  tone: string;
  sentAt?: string;
  openedAt?: string;
  completedAt?: string;
  declinedAt?: string;
  lastReminderAt?: string;
};

/** "Resident1" → "Resident 1"; leaves multi-word roles ("Designated Broker") intact. */
function roleLabel(role: string): string {
  return role.replace(/([A-Za-z])(\d)/g, '$1 $2');
}


/**
 * Who one party of a request IS — never the per-party key it is stored under
 * (docs/no-ids-in-ui.md).
 *
 * `RecipientActivity.recipient` is the writer's key, not a person: a signer role
 * for a signature document, but the AppFolio APPLICANT ID (a uuid) for a tenant
 * intake. This heading used to fall straight through to it, so an intake party
 * read as `6a557997-a192-11f1-…`. Name → role → email → a generic word; the id
 * is never the answer.
 */
function partyLabel(r: { name?: string; role?: string; email?: string; recipient: string }): string {
  if (r.name?.trim()) return r.name.trim();
  if (r.role?.trim()) return roleLabel(r.role.trim());
  if (r.email?.trim()) return r.email.trim();
  const raw = r.recipient.trim();
  if (raw.includes('@')) return raw;
  return OPAQUE_ID_RE.test(raw) ? 'Recipient' : roleLabel(raw);
}

/**
 * The most telling recent moment for a signer (signed > declined > opened >
 * sent), as a verb + the instant it happened. `at` is the precise ISO so the
 * modal can show date + time (WEL-47), not just a relative age.
 */
function signerActivity(s: SignerRow): { verb: string; at?: string } {
  if (s.completedAt) return { verb: 'Signed', at: s.completedAt };
  if (s.declinedAt) return { verb: 'Declined', at: s.declinedAt };
  if (s.openedAt) return { verb: 'Opened', at: s.openedAt };
  if (s.sentAt) return { verb: 'Sent', at: s.sentAt };
  return { verb: 'Not sent' };
}

/** The full signer roster for a signature-backed request, in signing order.
 *  When `swap` is set, each not-yet-signed signer offers the universal inline
 *  "Change email" (doc-type-scoped PATCH …/signers/:role, then re-invite). */
function SignersTable({
  signers,
  swap,
  onChanged,
}: {
  signers: SignerRow[];
  swap?: { groupId: string; documentType: CurrentDocumentType };
  onChanged?: () => void;
}): React.JSX.Element {
  const completed = signers.filter((s) => s.status === 'completed').length;
  return (
    <div className="stack-sm">
      <SectionTitle>
        Signers · {completed}/{signers.length} signed
      </SectionTitle>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Activity</th>
            </tr>
          </thead>
          <tbody>
            {signers.map((s, i) => (
              <tr key={`${s.role}-${i}`}>
                <td>
                  {s.name || '—'}
                  {s.email && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>{s.email}</div>
                  )}
                  {swap && s.status !== 'completed' && s.status !== 'declined' && (
                    <div
                      style={{
                        marginTop: 'var(--space-1)',
                        display: 'flex',
                        flexWrap: 'wrap',
                      }}
                    >
                      <SignerEmailSwap
                        inputId={`req-swap-${swap.groupId}-${s.role}-${i}`}
                        signerName={s.name || roleLabel(s.role)}
                        currentEmail={s.email ?? ''}
                        onSubmit={async (email) => {
                          await updateSignerEmail(
                            swap.groupId,
                            s.role,
                            email,
                            swap.documentType,
                          );
                          appToast.success(
                            `Email updated — ${s.name || roleLabel(s.role)} was re-invited at ${email}.`,
                          );
                        }}
                        {...(onChanged ? { onSaved: onChanged } : {})}
                      />
                    </div>
                  )}
                </td>
                <td>
                  {roleLabel(s.role)}
                  {s.order != null && (
                    <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
                      {' '}
                      · order {s.order}
                    </span>
                  )}
                </td>
                <td>
                  <span className="pill" data-tone={s.tone}>
                    {s.statusLabel}
                  </span>
                </td>
                <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                  {(() => {
                    const a = signerActivity(s);
                    if (!a.at) return a.verb;
                    return (
                      <span title={a.at}>
                        {a.verb} {fmtDateTime(a.at)}
                        <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>
                          {' · '}
                          {relativeAge(a.at)}
                        </span>
                      </span>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- small presentational helpers ------------------------------------------

function str(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function SectionTitle({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' }}>
      {children}
    </div>
  );
}

/** A smaller muted label for a sub-grouping within a section (e.g. RV "Screening"). */
function SubLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        color: 'var(--fg-subtle)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--weight-medium)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginTop: 'var(--space-2)',
      }}
    >
      {children}
    </div>
  );
}

function EmptyData({ label }: { label: string }): React.JSX.Element {
  return <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-sm)' }}>{label}</div>;
}

function KeyVal({ label, value, mono }: { label: string; value: string; mono?: boolean }): React.JSX.Element {
  return (
    <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'baseline' }}>
      <span style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', minWidth: '6rem' }}>{label}</span>
      <span style={{ fontFamily: mono ? 'var(--font-mono, monospace)' : undefined, fontSize: 'var(--text-sm)' }}>{value}</span>
    </div>
  );
}

/** Flat key/value grid over a record's own primitive entries (skips empties). */
function KeyValGrid({ obj, emptyLabel }: { obj: Record<string, unknown>; emptyLabel: string }): React.JSX.Element {
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== '' && typeof v !== 'object');
  if (entries.length === 0) return <EmptyData label={emptyLabel} />;
  return (
    <div className="table-wrap">
      <table className="table">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: 'var(--fg-muted)', width: '40%' }}>{humanize(k)}</td>
              <td>{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Per-key label overrides for fields whose humanized form reads poorly. */
const HUMANIZE_OVERRIDES: Record<string, string> = {
  addressLine1: 'Mailing address',
  addressLine2: 'Address line 2',
  zip: 'ZIP',
};

/** camelCase / snake_case → "Title case" for a label. */
function humanize(key: string): string {
  const override = HUMANIZE_OVERRIDES[key];
  if (override) return override;
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
